import {
  Box,
  Card,
  CardContent,
  Table,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import {AxiosError} from 'axios'
import {get, isEmpty, pick} from 'lodash'
import React, {useEffect, useRef, useState} from 'react'
import {useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {useQueryClient} from 'react-query'

import {usePersistContact} from '../../../common/react-query/hooks/mutations/usePersistContact'
import {usePersistOrderRequests} from '../../../common/react-query/hooks/mutations/usePersistOrderRequests'
import {BusinessLineType} from '../../../common/types'
import {useOrderIntakeStyles} from '../../../Hooks/OrderIntake/useOrderIntakeStyles'
import {ConfirmationScreen} from '../../../OrderIntake/components/ConfirmationScreen'
import {DeliveryProcessingOrderCard} from '../../../OrderIntake/components/Deliver/DeliveryProcessingOrderCard'
import {ShippingType} from '../../../OrderIntake/declarations/OrderIntake.enums'
import {useBulkCementOrderIntake} from '../../../Organisms/OrderIntake/BulkCementOrderIntake.provider'
import {BulkOrderFormValues} from '../../BulkOrdersFormValues'
import {
  GroupedPlacedDeliveries,
  OrderIntakePayload,
  OrderRequestGroupResponse,
  PersistedOrderRequest,
  QuantityType
} from '../../declarations/types'
import {OptionsKeys, V2OptionsKeys} from '../../Options'
import {groupPlacedDeliveries} from '../../../Organisms/OrderIntake/utils'
import {QuantityUomToSymbolComponent} from '../../../../src/Pages/Order/Order.components'
import {EvoZeroBadge} from '../../../Molecules/EvoZeroBadge'
import {useFeaturesState} from '../../../Organisms/Features'
import {useLocation} from 'react-router'
import {SplitLoadConfirmationScreen} from '../../components/SplitLoadConfirmationScreen'
import {SplitLoadOrderIntakePayload} from '../containers/SplitLoad/buildSplitLoadPayload'

interface HubErrorResponseData {
  detail: string
  status: string
  title: string
}
interface Props {
  businessLine: BusinessLineType
  quantityType: QuantityType
  quantityUom: string
  groupOrderSummaryDataArray: OrderRequestGroupResponse[]
  splitLoadPayloads?: SplitLoadOrderIntakePayload[]
  isLoading: boolean
  isError: boolean
  onClose: () => void
}

export const DeliveryOrderIntakeRequestConfirmation: React.FC<Props> = ({
  businessLine,
  quantityType,
  quantityUom,
  groupOrderSummaryDataArray,
  splitLoadPayloads,
  isLoading,
  isError,
  onClose
}) => {
  const {t} = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const {deliveries, orderSummary} = useBulkCementOrderIntake()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const isSplitLoadMode = searchParams.get('loadType') === 'splitLoad'

  const classes = useOrderIntakeStyles()
  const queryClient = useQueryClient()
  const optionsReloaded = useRef(false)
  const [customErrorMessage, setCustomErrorMessage] = useState<string | null>('')

  const {watch} = useFormContext<BulkOrderFormValues>()
  const selectedSite = watch('selectedSite')
  const mainPhone = get(deliveries[0], 'payload.contact.mainPhone', '')
  const backupPhone = get(deliveries[0], 'payload.contact.backupPhone', '')
  const userFullName = get(deliveries[0], 'payload.contact.fullName', '')
  const [showError, setShowError] = useState<boolean>(false)
  const siteNumber = watch('selectedSite.shippingAddress.siteNumber')
  const contact = watch('contact')
  const {mutate: persistContact} = usePersistContact()
  const {mutate: persistOrderRequests} = usePersistOrderRequests()
  const {getFeature} = useFeaturesState()

  const [groupedPlacedDeliveries, setGroupedPlacedDeliveries] = useState<GroupedPlacedDeliveries[]>(
    []
  )

  useEffect(() => {
    if (isEmpty(deliveries)) return
    setGroupedPlacedDeliveries(
      groupPlacedDeliveries(deliveries, selectedSite, groupOrderSummaryDataArray)
    )
    persistContact({siteNumber, contact})
    persistOrderRequests({
      siteNumber,
      orderRequests: deliveries.map((delivery) => ({
        ...(pick(delivery.payload, [
          'materialNumber',
          'materialEnteredNumber',
          'materialDescription',
          'capacity',
          'deliveryTime',
          'customerReference',
          'additionalDriverInfo',
          'contractItemPositionNumber',
          'isSendingConfirmationEmailUnChecked',
          'plantName',
          'contractNumber'
        ]) as PersistedOrderRequest)
      }))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveries, groupOrderSummaryDataArray])
  const onItemError = (error: AxiosError) => {
    setShowError(true)
    const enableV2Options = getFeature('EnableV2Options')

    // 'One or more errors occurred while creating the order, possible reasons: InvalidSlot'
    if (error.response?.data) {
      const errorData = error.response.data as HubErrorResponseData
      if (errorData.detail && errorData.detail.indexOf('Provided slot') > -1) {
        setCustomErrorMessage('invalidSlot')
        if (!optionsReloaded.current) {
          const enableOrderCreateApi = !!getFeature('EnableOrderCreateApi')
          const optionKeys = enableV2Options
            ? V2OptionsKeys.all(enableOrderCreateApi)
            : OptionsKeys.all()
          void queryClient.invalidateQueries(optionKeys)
          optionsReloaded.current = true
        }
      } else {
        setCustomErrorMessage(null)
      }
    }
  }

  const renderPlacedDeliveryItem = (
    placedDelivery: OrderIntakePayload,
    showLoad: boolean,
    index: number,
    groupedOrderSummary: OrderRequestGroupResponse | undefined
  ) => {
    return (
      <DeliveryProcessingOrderCard
        isMobile={isMobile}
        showLoad={showLoad}
        showTime={true}
        businessLine={businessLine}
        quantityUom={quantityUom}
        delivery={placedDelivery}
        groupedOrderSummary={groupedOrderSummary}
        isLoading={isLoading}
        isError={isError}
        itemOrder={`${t('orderIntake.delivery')} ${index + 1}`}
        onError={onItemError}
        customErrorMessage={
          customErrorMessage ? t(`orderIntake.customErrorMessage.${customErrorMessage}`) : undefined
        }
      />
    )
  }

  // Render split load confirmation screen if in split load mode
  if (isSplitLoadMode && splitLoadPayloads) {
    return (
      <SplitLoadConfirmationScreen
        orderResponses={groupOrderSummaryDataArray}
        splitLoadPayloads={splitLoadPayloads}
        onClose={onClose}
        userFullName={get(deliveries[0], 'payload.contact.fullName', '')}
        mainPhone={get(deliveries[0], 'payload.contact.mainPhone', '')}
        backupPhone={get(deliveries[0], 'payload.contact.backupPhone', '')}
        shippingType={ShippingType.DELIVER}
        deliveryAddress={watch('selectedSite.shippingAddress')}
        quantityUom={quantityUom}
      />
    )
  }

  // Render regular confirmation screen
  return (
    <ConfirmationScreen
      data-test-id="delivery-order-intake-confirmation-screen"
      showError={showError}
      deliveryAddress={watch('selectedSite.shippingAddress')}
      summaryItems={groupedPlacedDeliveries.map((group, index) => {
        return (
          <Card
            variant="outlined"
            className={classes.confirmationSummaryItem}
            key={`Deliver-Item-Order-Confirmation-Processing-${index}`}
          >
            <CardContent>
              <Box className={classes.confirmationMaterialHeader}>
                <Typography variant="caption">
                  {t('orderIntake.confirmationScreen.materialLoad')}
                </Typography>
                <Box display="flex" flexDirection="column" style={{marginTop: 8, gap: 2}}>
                  <EvoZeroBadge isCarbonSavingProduct={group.headline.isCarbonSavingProduct} />
                  <Typography variant="h4">
                    {`${group.headline.quantity}`}
                    {quantityType === QuantityType.Load ? (
                      ` ${t('orderIntake.loads', {count: group.headline.quantity})} `
                    ) : (
                      <QuantityUomToSymbolComponent uom={group.headline.capacityUom} />
                    )}
                    {` ${group.headline.description}`}
                  </Typography>
                </Box>
              </Box>

              <Table>
                {!isMobile && (
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      {group.orders.length > 1 && (
                        <TableCell>{t('orderIntake.materialOrder.load')}</TableCell>
                      )}
                      <TableCell>{t('orderIntake.materialOrder.date')}</TableCell>
                      <TableCell></TableCell>
                      <TableCell>{t('orderIntake.confirmationScreen.additionalInfo')}</TableCell>
                      <TableCell>{t('orderIntake.customerReference')}</TableCell>
                    </TableRow>
                  </TableHead>
                )}
                {group.orders.map((order: OrderIntakePayload, index: number) =>
                  renderPlacedDeliveryItem(
                    order,
                    group.orders.length > 1,
                    index,
                    group.groupedOrderSummary[index]
                  )
                )}
              </Table>
            </CardContent>
          </Card>
        )
      })}
      date={orderSummary[0]?.createdOn}
      onClose={onClose}
      mainPhone={mainPhone}
      backupPhone={backupPhone}
      userFullName={userFullName}
      shippingType={ShippingType.DELIVER}
      itemsNumber={deliveries.length}
    />
  )
}
